/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useEffect, useRef, useState } from 'react'

import CommentTemplateBanner from '../CommentTemplateBanner'
import { SecondaryButton } from '../../../../shared/buttons'
import { containerShadowCss } from '../../../../../lib/styles/shadows'
import { body6Css } from '../../../../../lib/styles/typography'
import useFocus from '../../../../../lib/hooks/useFocus'
import { isEmptyHtml, uniqueId } from '../../../../../lib/utils'
import RichTextField from '../../../../shared/input/RichTextField'
import { LabelVisibility } from '../../../../../types'

const addCommentCss = css`
  padding: 5px 20px 0 20px;

  .quill {
    padding: 8px !important;
    ${containerShadowCss};
    &:hover {
      /* This is the same as container shadow but I guess you can't use a variable with !important */
      box-shadow: 0px 0px 2px rgba(0, 0, 0, 0.14) !important;
    }
  }

  .rename {
    padding-left: 2px;
  }
`

const commentCss = css`
  margin-left: 0;
  padding-right: 8px;
  border-radius: 4px;
`

const buttonCss = css`
  display: flex;
  justify-content: flex-end;
`

const titleCss = css`
  padding: 20px 20px 10px 0;
  ${body6Css};
`

type AddCommentProps = {
  /** Comment text */
  comment: string
  /** Indicates if this plan is a template */
  isTemplate: boolean
  /** Max length of the comment */
  maxLength?: number
  /** Event callback when the cancel or close buttons are clicked */
  onCancel: () => void
  /** Event callback when the comment text changes */
  onCommentChange: (e: string) => void
  /** Event callback when the save comment button is clicked */
  onSaveComment: () => void
  /** Show the add comment field when clicking Add Comment from the group menu */
  showAddComment: boolean
  /** Show the comments when clicking on the comment count button */
  showComments: boolean
}

const defaultProps: Pick<AddCommentProps, 'maxLength'> = {
  maxLength: 200
}

const AddComment = ({
  comment,
  isTemplate,
  maxLength,
  onCancel,
  onCommentChange,
  onSaveComment,
  showAddComment,
  showComments
}: AddCommentProps) => {
  const [inputRef, setInputFocus] = useFocus()
  const [showError, setShowError] = useState(false)
  const [showControls, setShowControls] = useState(showAddComment)
  const [rows, setRows] = useState(showComments ? 1 : 3)
  const container = useRef<HTMLDivElement>(null)

  // For accessibility purposes, focus on the comment when add is clicked
  useEffect(() => {
    if (inputRef && showAddComment) {
      setInputFocus()
    }
  }, [inputRef, setInputFocus, showAddComment])

  const handleBlur = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target && container?.current?.contains((e as any).relatedTarget)) {
      // If the cancel or save buttons are clicked, don't hide the controls
      return
    }
    if (showComments && comment.length === 0) {
      setShowControls(false)
      setRows(1)
    }
  }

  const handleFocus = () => {
    setShowControls(true)
    setRows(3)
  }

  const handleSaveComment = () => {
    if (isEmptyHtml(comment)) {
      setShowError(true)
      return
    }

    onSaveComment()

    if (showComments) {
      setShowControls(false)
      setRows(1)

      // workaround: The RichTextField/quilljs doesn't fire blur event when any
      // outter button are clicked, so it's possible only add a comment once
      inputRef.current?.blur()
    }
  }

  return (
    <div css={addCommentCss}>
      <div css={titleCss}>Add Comment</div>
      {isTemplate && <CommentTemplateBanner />}
      {showError}
      <RichTextField
        css={commentCss}
        error={showError}
        helperText={showError ? 'Comment is required to save' : ''}
        id={`comment-${uniqueId()}`}
        label="Comment"
        labelVisibility={LabelVisibility.HIDDEN}
        maxLength={maxLength}
        name="comment"
        onBlur={handleBlur}
        onChange={onCommentChange}
        onFocus={handleFocus}
        placeholder="Type comment here"
        ref={inputRef}
        rows={rows}
        showRemainingCount
        value={comment}
      />
      {showControls && (
        <div>
          <div css={buttonCss} ref={container}>
            <SecondaryButton ariaLabel="Cancel adding the comment" onClick={onCancel}>
              Cancel
            </SecondaryButton>
            <SecondaryButton
              ariaLabel="Save the comment, comment text is required to save"
              onClick={handleSaveComment}
            >
              Add
            </SecondaryButton>
          </div>
        </div>
      )}
    </div>
  )
}

AddComment.defaultProps = defaultProps

export default AddComment
